/**
 * The seller-visible lifecycle of a listing. Order is the timeline/stepper order,
 * so a new status must be inserted at its real position, not appended.
 */
export const PROPERTY_STATUS_FLOW = [
  'draft',
  'submitted',
  'under_review',
  'lawyer_assigned',
  'lawyer_verified',
  'listed',
  'sold'
] as const;

export type PropertyStatus = (typeof PROPERTY_STATUS_FLOW)[number];

/** Statuses a non-owner may see. Nothing reaches the public list before a legal opinion. */
export const PUBLIC_PROPERTY_STATUSES = ['listed', 'lawyer_verified'] as const;

/**
 * Rows written before the rename still carry `verified`. Read paths map it;
 * write paths must emit the current name, or the legacy value spreads.
 */
export const LEGACY_PROPERTY_STATUS_MAP: Readonly<Record<string, PropertyStatus>> = Object.freeze({
  verified: 'lawyer_verified'
});

export function isPropertyStatus(value: unknown): value is PropertyStatus {
  return typeof value === 'string' && (PROPERTY_STATUS_FLOW as readonly string[]).includes(value);
}

/** Unknown input becomes `draft` rather than throwing — a stale row must still render. */
export function coercePropertyStatus(raw: unknown): PropertyStatus {
  const value = typeof raw === 'string' ? raw : 'draft';
  const mapped = LEGACY_PROPERTY_STATUS_MAP[value] ?? value;
  return isPropertyStatus(mapped) ? mapped : 'draft';
}

export type RiskLevel = 'low' | 'medium' | 'high';

/**
 * Legal-opinion risk score (0–100) to band. The thresholds are inclusive upper
 * bounds and are duplicated nowhere else: a lawyer's certificate and the buyer's
 * badge have to agree or the same property looks safe on one screen and risky on
 * the other.
 */
export const RISK_LEVEL_THRESHOLDS = Object.freeze({ low: 40, medium: 70 });

export function riskLevelFromScore(score: number): RiskLevel {
  if (score <= RISK_LEVEL_THRESHOLDS.low) return 'low';
  if (score <= RISK_LEVEL_THRESHOLDS.medium) return 'medium';
  return 'high';
}
